<template>
    <div v-if="user" class="profile-page-actions-container">

        <!-- Row 1: Call + WhatsApp/Email -->
        <div class="profile-page-actions-row" v-if="!isSeekingServicesProfile">
            <a v-if="isPhoneNumberVisible"
               @click.prevent="callClick"
               class="btn btn-rebrand-primary btn-rebrand-sm phone-number m-0"
               :class="{locked: profileIsLocked, notLocked: !profileIsLocked}"
               :href="urlPhone"
            >
                <icon-component v-if="profileUserIsCurrentUserAndBlurred" :icon="fa_lock()"
                                class="fa-lock" width="16px" height="16px"></icon-component>
                <icon-component v-else :icon="fa_phone_alt()" width="16px" height="16px"></icon-component>
                {{ callButtonText }}
            </a>

            <a v-if="showWhatsAppButton()" @click="onContactClick($event,'whatsapp', urlWhatsapp)" :href="urlWhatsapp"
               class="btn btn-rebrand-whatsapp btn-rebrand-sm profile-button-whatsapp m-0"
               :class="{locked: profileIsLocked, notLocked: !profileIsLocked}"
            >
                <icon-component v-if="profileUserIsCurrentUserAndBlurred" :icon="fa_lock()" class="fa-lock" width="16px"
                                height="16px"/>
                <icon-component v-else :icon="whatsappIcon()" width="16px" height="16px"/>
                {{ translate.account.sendAWhatsapp }}
            </a>

            <a v-if="showEmailButton()" :href="urlEmail"
               @click="onContactClick($event,'email', urlEmail)"
               class="btn btn-rebrand-grey-outline btn-rebrand-sm profile-button-email m-0"
               :class="{locked: profileIsLocked, notLocked: !profileIsLocked}"
            >
                <icon-component v-if="profileUserIsCurrentUserAndBlurred" :icon="fa_lock()" class="fa-lock" width="16px"
                                height="16px"/>
                <icon-component v-else :icon="houseIcon()" force-current-color width="16px" height="16px"/>
                {{ translate.account.email }}
            </a>
        </div>

        <!-- Row 2: Message + Telegram + SMS -->
        <div class="profile-page-actions-row">
            <template v-if="!isSeekingServicesProfile">
                <a v-if="isConversationButtonVisible && isSexChatActiveNow" @click.prevent="onMessageClick()"
                   href="#" class="btn-sext-me-now profile-button-conversation m-0"
                   e2e-id="button-start-new-conversation"
                   :class="{locked: profileIsLocked, notLocked: !profileIsLocked}"
                >
                    <span class="sext-me-text">
                        <strong class="sext-me-title">
                            <span class="sext-me-online-dot"></span>
                            {{ translate.account.sextMeNow }}
                        </strong>
                        <span class="sext-me-subtitle">{{ translate.account.onlineRepliesUnder2Min }}</span>
                    </span>
                </a>

                <a v-else-if="isConversationButtonVisible" @click.prevent="onMessageClick()"
                   href="#" class="btn btn-rebrand-grey-outline btn-rebrand-sm profile-button-conversation m-0"
                   e2e-id="button-start-new-conversation"
                   :class="{locked: profileIsLocked, notLocked: !profileIsLocked}"
                >
                    <icon-component v-if="profileUserIsCurrentUserAndBlurred" :icon="fa_lock()" class="fa-lock"
                                    width="16px" height="16px"/>
                    <icon-component :icon="erobellaE()" force-current-colorwidth="16px" height="16px"/>
                    {{ translate.account.chat }}
                </a>

                <a v-if="user && user.telegram" :href="urlTelegram"
                   @click="onContactClick($event,'telegram', urlTelegram)"
                   class="btn btn-rebrand-grey-outline btn-rebrand-sm profile-button-telegram m-0"
                   :class="{locked: profileIsLocked, notLocked: !profileIsLocked}"
                >
                    <icon-component v-if="profileUserIsCurrentUserAndBlurred" :icon="fa_lock()" class="fa-lock"
                                    width="16px" height="16px"/>
                    <icon-component v-else :icon="telegram()" width="16px" height="16px"/>
                    Telegram
                </a>

                <a v-if="showSmsButton" :href="urlSms" @click="onContactClick($event,'sms', urlSms)"
                   class="btn btn-rebrand-grey-outline btn-rebrand-sm profile-button-sms m-0 d-md-none"
                   :class="{locked: profileIsLocked, notLocked: !profileIsLocked}"
                >
                    <icon-component v-if="profileUserIsCurrentUserAndBlurred" :icon="fa_lock()" class="fa-lock"
                                    width="16px" height="16px"/>
                    <icon-component v-else :icon="messageTyping()" width="16px" height="16px"/>
                    SMS
                </a>
            </template>

            <a v-else-if="isConversationButtonVisible" @click.prevent="startPrivateConversation(user.id)"
               href="#" class="btn btn-rebrand-grey-outline btn-rebrand-sm profile-button-conversation m-0"
               :class="{locked: profileIsLocked, notLocked: !profileIsLocked}"
               e2e-id="button-start-new-conversation">
                <icon-component :icon="erobellaE()" force-current-colorwidth="16px" height="16px"/>
                {{ translate.account.message }}
            </a>
        </div>

    </div>
</template>

<script>

import xtrackingMixin from "@/components/mixins/xtrackingMixin";
import userMixin from "@/components/mixins/userMixin";
import localStorageMixin from "@/components/mixins/localStorageMixin";
import translateMixin from "../../mixins/translateMixin";
import userProfileMixin from "@/components/mixins/profile/userProfileMixin";
import {
    erobellaE,
    fa_lock,
    fa_phone_alt,
    houseIcon,
    messageTyping,
    telegram,
    whatsappIcon,
} from "@/core/constants/icons";
import IconComponent from "@/components/global/IconComponent.vue";
import {regionRelativeUrl} from "@/core/utils/helpers/regions/regionUrlHelper";
import {numberForWhatsApp, phoneNumbersCodeIsValid} from "@/core/utils/helpers/regions/regionCountryDialCodeHelper";
import {LocalStorageKeysEnum} from "@/core/constants/enums/localStorage/localStorageKeysEnum";
import {googleTagManager} from "@/core/utils/helpers/googleTagManager";

const CLICKS_BEFORE_SHOW_ADVERT = 5;

function showWhatsAppButton() {
    return this.userPhone && this.user.phone_on_whatsapp;
}

function showEmailButton() {
    // Email only shows when NO WhatsApp AND NO phone
    return !this.showWhatsAppButton() && !this.userPhone && this.userEmail;
}

/**
 * A private phone reaches the page only when it's on WhatsApp, and then only the WhatsApp button may use it.
 * A manager-set contact_phone was never hidden by phone_private, so it stays visible.
 */
function isPhoneNumberVisible() {
    if (!this.userPhone) {
        return false;
    }

    if (this.user.contact_phone) {
        return true;
    }

    return !this.user.phone_private;
}

function numberForSMS() {
    if (!this.isPhoneNumberVisible) {
        return false;
    }

    const phone = this.userPhone;

    if (phoneNumbersCodeIsValid(phone)) {
        return phone;
    }

    return false;
}

function trackEvent(category = "profile", action, label) {
    const postfix = this.profileIsLocked ? "_locked" : "";
    const categoryWithPostfix = `${category}${postfix}`;

    this.captureTrackingEvent(this.currentUser, categoryWithPostfix, action, label, this.user.id);
}

async function callClick() {
    this.trackEvent("profile", "calls", "click");
    googleTagManager.gtmEvent("call-button-click", this.user.id, this.profileIsLocked);

    if (this.profileIsLocked) {
        return this.$emit("showLockedProfileModal");
    }

    const shouldProceed = await this.showAdvertPopUpIfNeedTo();
    if (!shouldProceed) {
        return;
    }

    this.$emit("call-click", this.userPhone);
}

function startPrivateConversation(recipientUserId) {
    if (!this.isLoggedIn) {
        return this.$emit("show-login-modal");
    }

    window.location.href = regionRelativeUrl("/users/account/messages/" + utils.params({uid: recipientUserId}));
}

function isConversationButtonVisible() {
    if (_.get(this, 'user.is_under_chatter', false)) {
        return true;
    }

    if (_.get(this, 'user.user_data.message_button_disabled', false)) {
        return false;
    }

    // escort can always message punter
    if ((this.isEscort || this.isVenue) && this.isSeekingServicesProfile) {
        return true;
    }

    if (!this.isLoggedIn && this.profileWasActiveInLast7Days) {
        // we show the messaging button
        // but the startPrivateConversation method will trigger the login modal
        return true;
    }

    // only the following type of conversations are allowed:
    // escort|venue-to-punter
    // punter-to-escort|venue (if last sign in was in the last 7 days)

    // punter can message escort OR venue active in the last 7 days
    if (this.isPunter && !this.isSeekingServicesProfile && this.profileWasActiveInLast7Days) {
        return true;
    }

    return false;
}

function showSmsButton() {
    if (!this.numberForSMS) {
        return false;
    }

    const allOtherButtonsOnSameRowAreVisible = (this.isConversationButtonVisible && this.isSexChatActiveNow) && _.get(this, 'user.telegram');

    return !allOtherButtonsOnSameRowAreVisible;
}

function isSexChatActiveNow() {
    if (_.has(this.regionData, 'sectionsToHide.sexChatFilter')) {
        return false;
    }

    if (!_.get(this, 'user.escorts_profile.offers_sex_chat')) {
        return false;
    }

    return moment().tz('Europe/London').hour() >= 8;
}

function profileWasActiveInLast7Days() {
    const lastOnline = _.get(this, "user.last_login");

    if (!lastOnline) {
        return false;
    }

    return moment().isBefore(moment(lastOnline).add(7, "days"));
}

/**
 * @returns {string}
 */
function whatsappText() {
    if (this.isVenueProfile) {
        return this.fillTranslateParams(
            this.translate.account.whatsappVenueContactText,
            {
                username: this.user.username,
                originUrl: this.originUrlWithPath(regionRelativeUrl("/")),
            },
        );
    }

    return this.fillTranslateParams(
        this.translate.account.whatsappEscortContactText,
        {
            username: this.user.username,
            originUrl: this.originUrlWithPath(regionRelativeUrl("/")),
            city: this.user.city,
        },
    );
}

/**
 * @returns {string}
 */
function smsContactText() {
    return this.fillTranslateParams(
        this.translate.account.smsContactTextBody,
        {
            originUrl: this.originUrlWithPath(regionRelativeUrl("/")),
        },
    );
}

async function onMessageClick() {
    this.trackEvent("profile", "conversation", "click");
    googleTagManager.gtmEvent("message-button-click", this.user.id, this.profileIsLocked);

    if (this.profileIsLocked) {
        return this.$emit("showLockedProfileModal");
    }

    const shouldProceed = await this.showAdvertPopUpIfNeedTo();
    if (!shouldProceed) {
        return;
    }

    this.startPrivateConversation(this.user.id);
}

async function onContactClick($event, name, redirectUrl) {
    $event.preventDefault();
    this.trackEvent("profile", name, "click");
    googleTagManager.gtmEvent(`${name}-button-click`, this.user.id, this.profileIsLocked);

    if (this.profileIsLocked) {
        this.$emit("showLockedProfileModal");
        return;
    }

    const shouldProceed = await this.showAdvertPopUpIfNeedTo();
    if (!shouldProceed) {
        return;
    }

    if (redirectUrl) {
        window.location.href = redirectUrl;
    }
}

async function onRequestCustomContentClick() {
    this.trackEvent("profile", "request_custom_content", "click");

    if (this.profileIsLocked) {
        return this.$emit("showLockedProfileModal");
    }

    const shouldProceed = await this.showAdvertPopUpIfNeedTo();
    if (!shouldProceed) {
        return;
    }

    this.startPrivateConversation(this.user.id);
}

function showAdvertPopUpIfNeedTo() {
    return new Promise((resolve) => {
        try {
            if (this.isVenueProfile) {
                return resolve(true);
            }

            if (this.isVipPunter) {
                return resolve(true);
            }

            if (this.isLoggedIn && !this.isPunter) {
                return resolve(true);
            }

            const count = this.getLocalStorageObject(LocalStorageKeysEnum.PROFILE_CONTACT_CLICK_COUNTER);
            let clickCount = 1;
            let newValue = 1;

            if (count) {
                clickCount = parseInt(count);
                newValue = parseInt(count) + 1;
            }

            const expiration = moment().endOf('day').toISOString();
            this.setLocalStorageObject(LocalStorageKeysEnum.PROFILE_CONTACT_CLICK_COUNTER, newValue, expiration);

            if (clickCount >= CLICKS_BEFORE_SHOW_ADVERT) {
                return this.$emit('show-advert-popup', resolve);
            }

            resolve(true);
        } catch (e) {
            resolve(true);
        }
    });
}

export default {
    components: {IconComponent},
    computed: {
        ...mapState(["currentUser", "translate", "regionData"]),
        profileWasActiveInLast7Days,
        isConversationButtonVisible,
        isSexChatActiveNow,
        showSmsButton,
        isPhoneNumberVisible,
        numberForSMS,
        urlPhone() {
            return `tel:${this.userPhone}`;
        },
        urlEmail() {
            return `mailto:${this.userEmail}`;
        },
        urlSms() {
            return `sms:${(this.numberForSMS)}?&body=${encodeURIComponent(this.smsContactText())}`;
        },
        urlTelegram() {
            return `https://t.me/${_.get(this, "user.telegram")}`;
        },
        urlWhatsapp() {
            return `https://api.whatsapp.com/send/?phone=${numberForWhatsApp(this.userPhone)}&text=${encodeURIComponent(this.whatsappText())}`;
        },
        userPhone() {
            return this.user.contact_phone || this.user.phone;
        },
        userEmail() {
            return this.user.contact_email || this.user.email;
        },
        callButtonText() {
            return this.profileIsLocked ? this.translate.account.call : this.userPhone;
        },
    },
    props: {
        isSeekingServicesProfile: {
            type: Boolean,
            default: null,
        },
        user: {
            type: Object,
            default: null,
        },
        profileIsLocked: {
            type: Boolean | Number,
            default: false,
        },
    },
    methods: {
        houseIcon,
        whatsappIcon,
        erobellaE,
        messageTyping,
        fa_lock,
        fa_phone_alt,
        telegram,
        showWhatsAppButton,
        showEmailButton,
        trackEvent,
        callClick,
        startPrivateConversation,
        whatsappText,
        smsContactText,
        onMessageClick,
        onContactClick,
        showAdvertPopUpIfNeedTo,
        onRequestCustomContentClick,
    },
    mixins: [
        xtrackingMixin,
        userMixin,
        localStorageMixin,
        translateMixin,
        userProfileMixin,
    ],
};
</script>
